import { cn } from "@/lib/cn";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import "./Dialog.scss";

export const Dialog = ({
  open,
  closeOnOutsideClick,
  children,
  id,
  className = "",
}: {
  open: boolean;
  closeOnOutsideClick?: () => void;
  children: ReactNode | ReactNode[];
  id: string;
  className?: string;
}) => {
  "use no memo";
  const [isOpen, setIsOpen] = useState(false);
  const openDialogIds = useRef<string[]>([]);

  useEffect(() => {
    if (open && !isOpen && !openDialogIds.current.includes(id)) {
      setIsOpen(open);
      openDialogIds.current.push(id);
    } else if (!open && isOpen) {
      setIsOpen(open);
      openDialogIds.current = openDialogIds.current.filter(
        (openId) => openId !== id,
      );
    }
  }, [open, isOpen, id]);

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  });
  const { isMounted, styles } = useTransitionStyles(context);

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent: "mousedown",
    enabled: false,
    // outsidePress: true,
  });
  const { getFloatingProps } = useInteractions([click, role, dismiss]);
  const headingId = useId();
  const descriptionId = useId();

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && closeOnOutsideClick) {
      closeOnOutsideClick();
    }
  };

  return (
    <FloatingPortal>
      {isMounted && (
        <FloatingOverlay
          id="dialog-tVmwfoEin0nW2Xg"
          className={id}
          lockScroll
          onClick={handleOverlayClick}
          style={{ ...styles }}
        >
          <FloatingFocusManager context={context}>
            <div
              className={cn("tVmwfoEin0nW2Xg-main tw-outline-none", {
                [id]: true,
                [className]: !!className,
              })}
              ref={refs.setFloating}
              aria-labelledby={headingId}
              aria-describedby={descriptionId}
              {...getFloatingProps()}
            >
              {children}
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      )}
    </FloatingPortal>
  );
};
